import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, Plus, Tv, RefreshCw, Search, X, Maximize2, Minimize2, Volume2, VolumeX, Copy, Check, ExternalLink, Heart, Star, Zap, Radio, Film, Music, Baby, Globe, Dumbbell, Trophy, LayoutGrid, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiCall } from '../../services/api';
import Hls from 'hls.js';
import TVGuide from './TVGuide';
import { useLiveTVMiniPlayer } from '../../context/LiveTVMiniPlayerContext';

// Custom category tabs with mapped IPTV categories
const CUSTOM_TABS = [
  { 
    id: 'all', 
    name: 'All', 
    icon: Globe,
    keywords: [] // Empty means show all
  },
  { 
    id: 'favorites', 
    name: 'Favorites', 
    icon: Heart,
    keywords: ['FAVORITES'] // Special handling
  },
  { 
    id: 'sports', 
    name: 'Sports', 
    icon: Trophy,
    keywords: ['SPORT', 'NFL', 'NBA', 'MLB', 'NHL', 'ESPN', 'FOX SPORT', 'SOCCER', 'FOOTBALL', 'BOXING', 'UFC', 'WWE', 'CRICKET', 'TENNIS', 'GOLF', 'RACING', 'DAZN', 'BEIN']
  },
  { 
    id: 'news', 
    name: 'News', 
    icon: Radio,
    keywords: ['NEWS', 'CNN', 'MSNBC', 'FOX NEWS', 'ABC NEWS', 'CBS NEWS', 'NBC NEWS', 'BBC NEWS', 'SKY NEWS', 'AL JAZEERA']
  },
  { 
    id: 'movies', 
    name: 'Movies', 
    icon: Film,
    keywords: ['MOVIE', 'CINEMA', 'HBO', 'STARZ', 'SHOWTIME', 'CINEMAX', 'FILM', 'AMC', 'TCM', 'HALLMARK', 'LIFETIME', 'FX', 'TNT']
  },
  { 
    id: 'music', 
    name: 'Music', 
    icon: Music,
    keywords: ['MUSIC', 'MTV', 'VH1', 'CMT', 'BET', 'VEVO']
  },
  { 
    id: 'kids', 
    name: 'Kids', 
    icon: Baby,
    keywords: ['KID', 'DISNEY', 'NICK', 'CARTOON', 'BOOMERANG', 'PBS KIDS', 'BABY', 'JUNIOR', 'CHILDREN']
  },
  { 
    id: 'entertainment', 
    name: 'Entertainment', 
    icon: Zap,
    keywords: ['ENTERTAINMENT', 'GENERAL', 'PRIME', 'SLING', 'TUBI', 'NETFLIX', 'HULU', 'PEACOCK', 'PARAMOUNT', 'DISCOVERY', 'TLC', 'BRAVO', 'E!', 'USA', 'SYFY']
  },
  { 
    id: 'documentary', 
    name: 'Documentary', 
    icon: Globe,
    keywords: ['DOCUMENTARY', 'NATIONAL GEOGRAPHIC', 'HISTORY', 'SCIENCE', 'ANIMAL PLANET', 'TRAVEL', 'FOOD', 'HGTV', 'DIY']
  },
  { 
    id: 'lifestyle', 
    name: 'Lifestyle', 
    icon: Dumbbell,
    keywords: ['LIFESTYLE', 'COOKING', 'FOOD', 'TRAVEL', 'HOME', 'GARDEN', 'FASHION', 'BEAUTY', 'HEALTH', 'FITNESS', 'RELAX']
  }
];

// Helper function to clean channel names - removes quality indicators like ᴴᴰ/ᴿᴬᵂ ⁶⁰ᶠᵖˢ
const cleanChannelName = (name) => {
  if (!name) return name;
  // Remove common quality indicators and Unicode superscript characters
  return name
    .replace(/[\u1D34\u1D35\u1D30\u1D31\u1D32\u1D33\u1D36\u1D37\u1D38\u1D39\u1D3A\u1D3B\u1D3C\u1D3D\u1D3E\u1D3F\u1D40\u1D41\u1D42\u1D43\u1D44\u1D45\u1D46\u1D47\u1D48\u1D49\u1D4A\u1D4B\u1D4C\u1D4D\u1D4E\u1D4F\u1D50\u1D51\u1D52\u1D53\u1D54\u1D55\u1D56\u1D57\u1D58\u1D59\u1D5A\u1D5B\u1D5C\u1D5D\u1D5E\u1D5F\u1D60\u1D61\u1D62\u1D63\u1D64\u1D65\u1D66\u1D67\u1D68\u1D69\u1D6A\u2070\u2071\u2074\u2075\u2076\u2077\u2078\u2079\u207A\u207B\u207C\u207D\u207E\u207F]+/g, '')
    .replace(/[ᴴᴰᴿᴬᵂᶠᵖˢ⁰¹²³⁴⁵⁶⁷⁸⁹]+/g, '')
    .replace(/\s*\/\s*$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

const LiveTVSection = ({ apiUrl }) => {
  // Live TV Mini Player context for minimize/expand functionality
  const { 
    liveChannel, 
    isMinimized, 
    playChannel: playChannelInMiniPlayer, 
    minimizePlayer, 
    closePlayer: closeMiniPlayer,
    isMuted: miniPlayerMuted,
    setIsMuted: setMiniPlayerMuted
  } = useLiveTVMiniPlayer();
  
  const [channels, setChannels] = useState([]);
  const [allChannels, setAllChannels] = useState([]); // Store all channels for filtering
  const [filteredChannels, setFilteredChannels] = useState([]); // Channels after filtering (before pagination)
  const [categories, setCategories] = useState(['All']);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('favorites'); // Default to Favorites
  const [searchQuery, setSearchQuery] = useState('');
  const [isDemo, setIsDemo] = useState(false);
  const [demoMessage, setDemoMessage] = useState('');
  
  // Local player state (for channels without mini player support)
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [streamError, setStreamError] = useState('');
  const [copied, setCopied] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  
  // View mode: 'grid' or 'guide' - Default to Guide view
  const [viewMode, setViewMode] = useState('guide');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const CHANNELS_PER_PAGE = 100;
  
  // Favorites stored in database (persistent even after clearing browser data)
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);
  
  // Featured live channel with EPG data for hero section
  const [featuredLive, setFeaturedLive] = useState(null);
  const [featuredProgram, setFeaturedProgram] = useState(null);
  const [heroMuted, setHeroMuted] = useState(true);
  const [heroVideoFailed, setHeroVideoFailed] = useState(false);
  
  // Refs for HLS player
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const heroVideoRef = useRef(null);
  const heroHlsRef = useRef(null);
  const playerContainerRef = useRef(null);
  
  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);
  
  // Toggle fullscreen
  const toggleFullscreen = () => {
    const container = playerContainerRef.current;
    if (!container) return;
    
    if (!document.fullscreenElement) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.mozRequestFullScreen) {
        container.mozRequestFullScreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  // Load favorites from database on mount
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        // First try to load from database
        const response = await apiCall('/iptv/favorites');
        if (response.status === 'success') {
          const dbFavorites = response.favorites || [];
          
          // Also check localStorage for any that might not be synced yet
          const localFavorites = JSON.parse(localStorage.getItem('iptv_favorites') || '[]');
          
          // Merge both (database takes priority, but don't lose local ones)
          const merged = [...new Set([...dbFavorites, ...localFavorites])];
          
          if (localFavorites.length > 0 && merged.length > dbFavorites.length) {
            // Sync local favorites to database
            await apiCall('/iptv/favorites/sync', {
              method: 'POST',
              body: JSON.stringify(merged),
              headers: { 'Content-Type': 'application/json' }
            });
            console.log('Synced local favorites to database');
          }
          
          setFavorites(merged);
          localStorage.setItem('iptv_favorites', JSON.stringify(merged));
          console.log('Loaded favorites from database:', merged.length, 'channels');
        }
      } catch (error) {
        console.error('Error loading favorites from database:', error);
        // Fallback to localStorage
        const localFavorites = JSON.parse(localStorage.getItem('iptv_favorites') || '[]');
        setFavorites(localFavorites);
      }
      setFavoritesLoaded(true);
    };
    
    loadFavorites();
  }, []);

  // Toggle favorite - saves to both database and localStorage
  const toggleFavorite = async (channelId) => {
    const isCurrentlyFavorite = favorites.includes(channelId);
    
    // Optimistic update
    const newFavorites = isCurrentlyFavorite
      ? favorites.filter(id => id !== channelId)
      : [...favorites, channelId];
    
    setFavorites(newFavorites);
    localStorage.setItem('iptv_favorites', JSON.stringify(newFavorites));
    
    // Sync to database
    try {
      if (isCurrentlyFavorite) {
        await apiCall(`/iptv/favorites/remove?channel_id=${channelId}`, { method: 'POST' });
        console.log('Removed from database favorites:', channelId);
      } else {
        await apiCall(`/iptv/favorites/add?channel_id=${channelId}`, { method: 'POST' });
        console.log('Added to database favorites:', channelId);
      }
    } catch (error) {
      console.error('Error syncing favorite to database:', error);
      // Keep local state anyway
    }
  };

  const isFavorite = (channelId) => favorites.includes(channelId);

  // Handle reordering favorites via drag and drop
  const handleReorderFavorites = async (newFavoritesOrder) => {
    // Update local state immediately
    setFavorites(newFavoritesOrder);
    localStorage.setItem('iptv_favorites', JSON.stringify(newFavoritesOrder));
    
    // Sync the new order to the database
    try {
      await apiCall('/iptv/favorites/reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ favorites: newFavoritesOrder })
      });
      console.log('Favorites order saved to database');
    } catch (error) {
      console.error('Error saving favorites order to database:', error);
      // Keep local state anyway
    }
  };

  // Set featured channel from favorites - always use the first favorite
  useEffect(() => {
    const setFeaturedChannelFromFavorites = async () => {
      if (favorites.length > 0) {
        try {
          // Always use the first favorite channel
          const channelId = favorites[0];
          const channelsData = await apiCall(`/iptv/channels/by-ids?ids=${channelId}`);
          if (channelsData.status === 'success' && channelsData.channels?.length > 0) {
            const channel = channelsData.channels[0];
            const streamResponse = await apiCall(`/iptv/stream/${channel.id}`);
            const streamUrl = streamResponse.stream_url || streamResponse.channel?.url;
            if (streamUrl) {
              setFeaturedLive({
                ...channel,
                url: streamUrl
              });
              setHeroVideoFailed(false); // Reset failure state
              console.log('Featured channel set to first favorite:', channel.name);
            }
          }
        } catch (error) {
          console.error('Error setting featured channel:', error);
        }
      }
    };
    
    setFeaturedChannelFromFavorites();
  }, [favorites]);

  // Fetch EPG program data for the featured channel (don't change the channel itself)
  useEffect(() => {
    const fetchFeaturedProgram = async () => {
      if (!featuredLive?.id) return;
      
      try {
        // Get EPG data for the featured channel
        const epgData = await apiCall(`/iptv/epg?stream_ids=${featuredLive.id}&limit=5`);
        
        if (epgData.status === 'success' && epgData.programs?.length > 0) {
          const now = Date.now();
          
          // Find currently airing program for the featured channel
          const liveProgram = epgData.programs.find(program => {
            const startTime = program.start_timestamp ? program.start_timestamp * 1000 : 
              new Date(program.start?.replace(' ', 'T') + 'Z').getTime();
            const endTime = program.stop_timestamp ? program.stop_timestamp * 1000 : 
              new Date(program.end?.replace(' ', 'T') + 'Z').getTime();
            return now >= startTime && now < endTime;
          });
          
          if (liveProgram) {
            setFeaturedProgram(liveProgram);
            console.log('Featured program set:', liveProgram.title);
          }
        }
      } catch (error) {
        console.error('Error fetching featured program:', error);
      }
    };
    
    fetchFeaturedProgram();
  }, [featuredLive?.id]);
  
  // Setup hero background video player
  useEffect(() => {
    if (!featuredLive?.id || !heroVideoRef.current) return;
    
    const video = heroVideoRef.current;
    
    // Cleanup previous HLS instance
    if (heroHlsRef.current) {
      heroHlsRef.current.destroy();
      heroHlsRef.current = null;
    }
    
    // Use the proxy URL to avoid CORS issues
    const proxyUrl = `/api/iptv/proxy/${featuredLive.id}.m3u8`;
    console.log('Loading hero video from proxy:', proxyUrl);
    
    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 30,
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        // Increase retry attempts for better resilience
        fragLoadingMaxRetry: 6,
        manifestLoadingMaxRetry: 6,
        levelLoadingMaxRetry: 6,
        // Retry delay settings
        fragLoadingRetryDelay: 1000,
        manifestLoadingRetryDelay: 1000,
        // Timeout settings
        fragLoadingTimeOut: 20000,
        manifestLoadingTimeOut: 20000,
        // Live stream settings
        liveSyncDurationCount: 3,
        liveMaxLatencyDurationCount: 10,
        liveDurationInfinity: true,
        startPosition: -1,
      });
      
      hls.loadSource(proxyUrl);
      hls.attachMedia(video);
      
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('Hero video manifest parsed, attempting playback...');
        video.play().catch(e => console.log('Hero autoplay prevented:', e));
      });
      
      hls.on(Hls.Events.ERROR, (event, data) => {
        console.log('Hero video error:', data.type, data.details, data.fatal);
        
        if (data.fatal) {
          // For any fatal error, just show the fallback gradient
          // Don't switch channels - keep showing the first favorite's info
          console.log('Hero video failed, showing fallback gradient');
          setHeroVideoFailed(true);
          hls.destroy();
          heroHlsRef.current = null;
        }
      });
      
      heroHlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = proxyUrl;
      video.play().catch(e => console.log('Hero autoplay prevented:', e));
    }
    
    return () => {
      if (heroHlsRef.current) {
        heroHlsRef.current.destroy();
        heroHlsRef.current = null;
      }
    };
  }, [featuredLive?.id]);

  // Filter channels by selected tab
  const filterChannelsByTab = useCallback((channelsList, tab) => {
    if (tab === 'all') {
      // Return all channels - limiting will be done in the effect
      return channelsList;
    }
    if (tab === 'favorites') {
      return channelsList.filter(c => favorites.includes(c.id));
    }
    
    const tabConfig = CUSTOM_TABS.find(t => t.id === tab);
    if (!tabConfig || tabConfig.keywords.length === 0) return channelsList;
    
    return channelsList.filter(channel => {
      const categoryUpper = (channel.category || '').toUpperCase();
      const nameUpper = (channel.name || '').toUpperCase();
      
      return tabConfig.keywords.some(keyword => 
        categoryUpper.includes(keyword) || nameUpper.includes(keyword)
      );
    });
  }, [favorites]);

  // Fetch all IPTV channels once
  const fetchAllChannels = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all channels without limit
      const data = await apiCall('/iptv/channels');

      if (data.status === 'demo') {
        setIsDemo(true);
        setDemoMessage(data.message || 'Showing demo channels');
      } else {
        setIsDemo(false);
        setDemoMessage('');
      }

      const fetchedChannels = data.channels || [];
      setAllChannels(fetchedChannels);
      
      // Apply current tab filter
      const filtered = filterChannelsByTab(fetchedChannels, selectedTab);
      setChannels(filtered);
      
      if (data.categories) {
        setCategories(['All', ...data.categories.filter(c => c !== 'All')]);
      }
    } catch (error) {
      console.error('Error fetching IPTV channels:', error);
      setIsDemo(true);
      setDemoMessage('Failed to connect to IPTV service');
    } finally {
      setLoading(false);
    }
  }, [selectedTab, filterChannelsByTab]);

  // Effect to filter channels when tab changes
  useEffect(() => {
    if (allChannels.length > 0) {
      let filtered = filterChannelsByTab(allChannels, selectedTab);
      
      // Apply search filter - searches entire database
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        filtered = filtered.filter(c => 
          c.name?.toLowerCase().includes(searchLower) ||
          c.category?.toLowerCase().includes(searchLower)
        );
      }
      
      // Store all filtered channels for pagination
      setFilteredChannels(filtered);
      
      // Reset to page 1 when filter changes
      setCurrentPage(1);
      
      // Apply pagination - show only current page
      const startIndex = 0;
      const endIndex = CHANNELS_PER_PAGE;
      setChannels(filtered.slice(startIndex, endIndex));
    }
  }, [selectedTab, searchQuery, allChannels, filterChannelsByTab]);

  // Effect to handle page changes
  useEffect(() => {
    if (filteredChannels.length > 0) {
      const startIndex = (currentPage - 1) * CHANNELS_PER_PAGE;
      const endIndex = startIndex + CHANNELS_PER_PAGE;
      setChannels(filteredChannels.slice(startIndex, endIndex));
    }
  }, [currentPage, filteredChannels]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredChannels.length / CHANNELS_PER_PAGE);

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of channel grid
      window.scrollTo({ top: 400, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    fetchAllChannels();
  }, []);

  // When tab changes, automatically switch view mode
  // Guide view is only available for Favorites tab
  useEffect(() => {
    if (selectedTab !== 'favorites' && viewMode === 'guide') {
      setViewMode('grid');
    }
  }, [selectedTab, viewMode]);

  // Initialize HLS player when a channel is selected
  useEffect(() => {
    if (isPlaying && selectedChannel?.id && videoRef.current) {
      const video = videoRef.current;
      // Use relative URL for the proxy to avoid CORS issues
      const streamUrl = `/api/iptv/proxy/${selectedChannel.id}.m3u8`;
      
      // Cleanup previous HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      
      setStreamError('');
      
      // Use HLS.js for playback
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 30,
          maxBufferLength: 60,
          maxMaxBufferLength: 120,
          startLevel: -1, // Auto quality
          capLevelToPlayerSize: true,
          debug: false,
          // More lenient error handling
          fragLoadingMaxRetry: 6,
          manifestLoadingMaxRetry: 6,
          levelLoadingMaxRetry: 6,
          fragLoadingRetryDelay: 1000,
          manifestLoadingRetryDelay: 1000,
          // Timeout settings
          fragLoadingTimeOut: 20000,
          manifestLoadingTimeOut: 20000,
          levelLoadingTimeOut: 20000,
          // Live stream settings
          liveSyncDurationCount: 3,
          liveMaxLatencyDurationCount: 10,
          liveDurationInfinity: true,
          startPosition: -1,
        });
        
        hls.loadSource(streamUrl);
        hls.attachMedia(video);
          
        hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
          console.log('HLS manifest loaded, levels:', data.levels?.length);
          video.play().catch(err => {
            console.error('Autoplay failed:', err);
            setStreamError('Click play button to start');
          });
        });
        
        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('HLS error:', data.type, data.details);
          
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                if (retryCount < 3) {
                  setStreamError('Loading stream...');
                  setRetryCount(prev => prev + 1);
                  setTimeout(() => hls.startLoad(), 2000);
                } else {
                  setStreamError('Network error. Try copying the URL to your IPTV app.');
                }
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                if (retryCount < 2) {
                  setStreamError('Buffering...');
                  setRetryCount(prev => prev + 1);
                  hls.recoverMediaError();
                } else {
                  setStreamError('This channel uses an unsupported codec. Copy URL to play in your IPTV app.');
                }
                break;
              default:
                setStreamError('Stream unavailable. Try another channel or copy URL.');
                break;
            }
          }
        });
        
        hls.on(Hls.Events.FRAG_LOADED, () => {
          // Clear error when fragments start loading
          if (streamError && streamError.includes('Loading')) {
            setStreamError('');
          }
        });
          
        hlsRef.current = hls;
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari native HLS support
        video.src = streamUrl;
        video.addEventListener('loadedmetadata', () => {
          video.play().catch(err => console.error('Autoplay failed:', err));
        });
        video.addEventListener('error', () => {
          setStreamError('Playback error. Copy URL to play in your IPTV app.');
        });
      } else {
        setStreamError('HLS playback not supported. Copy URL to play in your IPTV app.');
      }
    }
    
    // Cleanup on unmount or channel change
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      setRetryCount(0);
    };
  }, [isPlaying, selectedChannel]);

  // Refresh channels
  const handleRefresh = async () => {
    setLoading(true);
    try {
      await apiCall('/iptv/refresh', { method: 'POST' });
      await fetchAllChannels();
    } catch (error) {
      console.error('Error refreshing channels:', error);
    }
  };

  // Play channel - now uses the mini player context for persistence across pages
  const handlePlayChannel = async (channel) => {
    if (channel.is_demo || !channel.url) {
      // Demo channel - show message (use local state)
      setSelectedChannel(channel);
      setIsPlaying(false);
      setStreamError('');
    } else {
      // Close local player if open
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      setSelectedChannel(null);
      setIsPlaying(false);
      
      // Play in mini player (supports minimize/expand)
      playChannelInMiniPlayer(channel);
    }
  };

  // Copy stream URL to clipboard
  const handleCopyUrl = async () => {
    const channelToCopy = liveChannel || selectedChannel;
    if (channelToCopy?.url) {
      try {
        await navigator.clipboard.writeText(channelToCopy.url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  // Close player
  const handleClosePlayer = () => {
    // Close local player
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    setSelectedChannel(null);
    setIsPlaying(false);
    setStreamError('');
    setCopied(false);
    setRetryCount(0);
    
    // Also close mini player if open
    closeMiniPlayer();
  };

  // Get featured channel - prefer first favorite with data, then first channel in current view, then first from all channels
  const getFeaturedChannel = () => {
    // First, try to get from current filtered channels
    if (channels.length > 0) return channels[0];
    // Then try allChannels
    if (allChannels.length > 0) return allChannels[0];
    return null;
  };
  const featuredChannel = getFeaturedChannel();

  // Get total channel count for display
  const totalChannelCount = allChannels.length || channels.length;
  const totalCategoryCount = categories.length - 1;

  return (
    <div className="space-y-6">
      {/* Video Player Modal */}
      {selectedChannel && (
        <div className={`fixed inset-0 z-50 bg-black flex items-center justify-center ${isFullscreen ? 'p-0' : 'p-4'}`}>
          <div 
            ref={playerContainerRef}
            className={`relative bg-black flex flex-col ${
              isFullscreen 
                ? 'w-full h-full max-w-none rounded-none' 
                : 'w-full max-w-5xl rounded-2xl overflow-hidden border border-white/20'
            }`}
          >
            {/* Header - channel info + close */}
            <div className="relative z-20 px-4 py-3 bg-black/90 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                {selectedChannel.logo && (
                  <img 
                    src={selectedChannel.logo} 
                    alt={selectedChannel.name}
                    className="w-8 h-8 object-contain bg-white/10 rounded-lg p-1"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
                <div>
                  <h3 className="text-white font-bold text-sm">{cleanChannelName(selectedChannel.name)}</h3>
                  <p className="text-gray-400 text-xs">{selectedChannel.category}</p>
                </div>
              </div>
              <button
                onClick={handleClosePlayer}
                data-testid="livetv-close-btn"
                className="p-2 bg-white/10 rounded-full hover:bg-red-600 transition-colors"
                title="Close Player"
              >
                <X size={20} className="text-white" />
              </button>
            </div>

            {/* Video Area */}
            <div className={`bg-black flex items-center justify-center relative ${isFullscreen ? 'flex-1 w-full' : 'aspect-video'}`}>
              {isPlaying && selectedChannel.url ? (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    muted={isMuted}
                    className="w-full h-full object-contain"
                    playsInline
                    style={{ pointerEvents: 'none' }}
                  />
                  {streamError && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-10">
                      <div className="text-center p-6 max-w-md">
                        <Tv size={48} className="text-cyan-500 mx-auto mb-4" />
                        <h4 className="text-white font-semibold mb-2">Stream Not Compatible</h4>
                        <p className="text-gray-400 mb-4 text-sm">
                          This channel uses a codec not supported by browsers. 
                          Use one of the options below to watch:
                        </p>
                        <div className="flex flex-col gap-3">
                          <button 
                            onClick={handleCopyUrl}
                            className="px-4 py-2 bg-cyan-600 rounded-lg text-white hover:bg-cyan-700 transition-colors flex items-center justify-center gap-2"
                          >
                            {copied ? <Check size={18} /> : <Copy size={18} />}
                            {copied ? 'Copied!' : 'Copy URL for IBO Player'}
                          </button>
                          <a 
                            href={`vlc://${selectedChannel.url}`}
                            className="px-4 py-2 bg-orange-600 rounded-lg text-white hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
                          >
                            <ExternalLink size={18} />
                            Open in VLC
                          </a>
                          <button 
                            onClick={() => {
                              setRetryCount(0);
                              setStreamError('');
                              if (hlsRef.current) {
                                hlsRef.current.startLoad();
                              }
                            }}
                            className="px-4 py-2 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors"
                          >
                            Try Again Anyway
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center p-8">
                  <Tv size={64} className="text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">
                    {selectedChannel.is_demo ? 'Demo Channel' : 'Stream Unavailable'}
                  </h3>
                  <p className="text-gray-400 mb-4">
                    {selectedChannel.is_demo 
                      ? 'This is a demo channel. Connect your IPTV service to watch live.'
                      : 'Unable to load stream. Copy URL to play in IBO Player.'}
                  </p>
                  {!selectedChannel.is_demo && selectedChannel.url && (
                    <button 
                      onClick={handleCopyUrl}
                      className="px-4 py-2 bg-cyan-600 rounded-lg text-white hover:bg-cyan-700 transition-colors flex items-center justify-center gap-2 mx-auto"
                    >
                      {copied ? <Check size={18} /> : <Copy size={18} />}
                      {copied ? 'Copied!' : 'Copy Stream URL'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Single Controls Bar - always at bottom */}
            <div className="relative z-20 px-4 py-3 bg-black/90 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setIsMuted(!isMuted);
                    if (videoRef.current) {
                      videoRef.current.muted = !isMuted;
                    }
                  }}
                  data-testid="livetv-mute-btn"
                  className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                  title={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? <VolumeX size={20} className="text-white" /> : <Volume2 size={20} className="text-white" />}
                </button>
                <button
                  onClick={handleCopyUrl}
                  data-testid="livetv-copy-url-btn"
                  className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-2 text-sm ${
                    copied ? 'bg-green-600 text-white' : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  title="Copy stream URL"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied!' : 'Copy URL'}
                </button>
              </div>
              <button 
                onClick={toggleFullscreen}
                data-testid="livetv-fullscreen-btn"
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg transition-colors flex items-center gap-2"
                title={isFullscreen ? "Exit Fullscreen (Esc)" : "Enter Fullscreen"}
              >
                {isFullscreen ? <Minimize2 size={20} className="text-white" /> : <Maximize2 size={20} className="text-white" />}
                <span className="text-white text-sm font-medium">{isFullscreen ? 'Exit' : 'Fullscreen'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live TV Hero Section - Shows first live channel with video background */}
      {(featuredLive || featuredChannel || loading) && (
        <div className="relative rounded-2xl overflow-hidden border border-white/20 bg-black hover:border-white/40 hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] transition-all duration-300">
          <div className="relative h-[250px] md:h-[350px] overflow-hidden">
            {/* Video Background - only show if not failed */}
            {featuredLive?.id && !heroVideoFailed && (
              <video
                ref={heroVideoRef}
                autoPlay
                loop
                muted={heroMuted}
                playsInline
                className="absolute inset-0 w-full h-full object-cover z-0"
              />
            )}
            
            {/* Animated gradient background when video fails or no featured live */}
            {(!featuredLive?.id || heroVideoFailed) && (
              <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-900 via-purple-900 to-black animate-pulse" style={{ animationDuration: '4s' }} />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-cyan-500/20 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-red-600/30 to-transparent animate-pulse" style={{ animationDuration: '2s' }} />
              </div>
            )}
            
            {/* Gradient Overlays - slightly more transparent to show video */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent z-10" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 z-10" />
            
            {/* Content */}
            <div className="absolute inset-0 flex items-center z-20">
              <div className="p-6 md:p-10 max-w-xl">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded animate-pulse">LIVE</span>
                  <span className="text-gray-300 text-sm">{cleanChannelName(featuredLive?.category || featuredChannel?.category) || 'Live TV'}</span>
                </div>
                
                {/* Show Program Title if available */}
                {featuredProgram ? (
                  <>
                    <h1 className="text-3xl md:text-5xl font-bold text-white mb-2">
                      {cleanChannelName(featuredProgram.title) || 'Live Program'}
                    </h1>
                    <p className="text-cyan-400 text-sm md:text-base font-medium mb-1">
                      {cleanChannelName(featuredLive?.name) || cleanChannelName(featuredChannel?.name)}
                    </p>
                    <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                      {featuredProgram.description || `${favorites.length} favorites • ${totalChannelCount > 0 ? `${totalChannelCount} channels` : 'Loading...'}`}
                    </p>
                    {/* Program Time */}
                    <p className="text-gray-400 text-xs mb-4">
                      {featuredProgram.start_timestamp && featuredProgram.stop_timestamp && (
                        <>
                          {new Date(featuredProgram.start_timestamp * 1000).toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit', 
                            hour12: true,
                            timeZone: 'America/Chicago'
                          })} - {new Date(featuredProgram.stop_timestamp * 1000).toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit', 
                            hour12: true,
                            timeZone: 'America/Chicago'
                          })} CST
                        </>
                      )}
                    </p>
                  </>
                ) : (
                  <>
                    <h1 className="text-3xl md:text-5xl font-bold text-white mb-2">
                      {cleanChannelName(featuredLive?.name) || cleanChannelName(featuredChannel?.name) || 'Live Television'}
                    </h1>
                    <p className="text-gray-300 text-sm md:text-base mb-4">
                      {favorites.length} favorites • {totalChannelCount > 0 ? `${totalChannelCount} channels` : 'Loading...'} • {totalCategoryCount > 0 ? `${totalCategoryCount} categories` : ''}
                    </p>
                  </>
                )}
                
                <div className="flex items-center gap-3 sm:gap-4">
                  {(featuredLive || featuredChannel) && (
                    <button 
                      onClick={() => handlePlayChannel(featuredLive || featuredChannel)}
                      className="flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white text-black rounded-lg font-bold hover:bg-gray-200 transition-colors text-sm sm:text-base"
                    >
                      <Play size={18} fill="black" />
                      Watch Now
                    </button>
                  )}
                  <button 
                    onClick={handleRefresh}
                    className="flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white/10 border border-white/30 text-white rounded-lg font-medium hover:bg-white/20 transition-colors text-sm sm:text-base"
                  >
                    <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                    Refresh
                  </button>
                </div>
              </div>
            </div>
            
            {/* Mute/Unmute button for hero video - only show when video is playing */}
            {featuredLive?.id && !heroVideoFailed && (
              <button
                onClick={() => setHeroMuted(!heroMuted)}
                className="absolute bottom-4 right-4 z-30 p-2 bg-black/60 rounded-full hover:bg-black/80 transition-colors"
                title={heroMuted ? 'Unmute' : 'Mute'}
              >
                {heroMuted ? (
                  <VolumeX size={20} className="text-white" />
                ) : (
                  <Volume2 size={20} className="text-white" />
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* IPTV Status Message */}
      {isDemo && (
        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4 flex items-start gap-3">
          <Tv className="text-yellow-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="text-yellow-400 font-medium">IPTV Service Status</h4>
            <p className="text-yellow-400/70 text-sm mt-1">{demoMessage}</p>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="flex flex-col gap-3 sm:gap-4">
        {/* View Mode Toggle + Custom Category Tabs - Stack on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          {/* Category tabs - Scrollable on mobile */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide flex-1 -mx-1 px-1">
            {CUSTOM_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = selectedTab === tab.id;
              const count = tab.id === 'favorites' ? favorites.length : null;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl whitespace-nowrap text-xs sm:text-sm font-medium transition-all duration-300 border ${
                    isActive 
                      ? tab.id === 'favorites' 
                        ? 'bg-red-600 text-white border-red-600' 
                        : 'bg-white text-black border-white'
                      : 'bg-black text-white border-white/30 hover:border-white/60 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)]'
                  }`}
                >
                  <Icon size={14} fill={isActive && tab.id === 'favorites' ? 'white' : 'none'} />
                  <span className="hidden xs:inline sm:inline">{tab.name}</span>
                  {count !== null && count > 0 && (
                    <span className={`ml-0.5 sm:ml-1 px-1 sm:px-1.5 py-0.5 text-[10px] sm:text-xs rounded-full ${
                      isActive ? 'bg-white/20' : 'bg-white/10'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* View Mode Toggle - Compact on mobile */}
          {selectedTab === 'favorites' && (
            <div className="flex-shrink-0 flex items-center gap-1 p-1 bg-white/5 rounded-lg border border-white/10 self-start sm:self-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-cyan-600 text-white' 
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
                title="Grid View"
                data-testid="grid-view-btn"
              >
                <LayoutGrid size={16} />
                <span>Grid</span>
              </button>
              <button
                onClick={() => setViewMode('guide')}
                className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${
                  viewMode === 'guide' 
                    ? 'bg-cyan-600 text-white' 
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
                title="TV Guide View"
                data-testid="guide-view-btn"
              >
                <List size={16} />
                <span>Guide</span>
              </button>
            </div>
          )}
        </div>

        {/* Search - only show in grid mode */}
        {viewMode === 'grid' && (
          <div className="relative max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search channels..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-black border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:shadow-[0_0_15px_rgba(6,182,212,0.2)] transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X size={16} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* TV Guide View */}
      {viewMode === 'guide' ? (
        <TVGuide 
          key={selectedTab === 'favorites' ? 'favorites-guide' : 'all-guide'}
          onPlayChannel={handlePlayChannel}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          showFavoritesOnly={selectedTab === 'favorites'}
          onReorderFavorites={handleReorderFavorites}
        />
      ) : (
        /* Channels Grid */
        loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <RefreshCw className="animate-spin mx-auto mb-4 text-cyan-400" size={32} />
            <p className="text-gray-400">Loading channels...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-4">
          {channels.map((channel) => (
            <div
              key={channel.id}
              className="group bg-black border border-white/10 rounded-xl p-2 sm:p-4 text-left hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(6,182,212,0.15)] transition-all duration-300 relative"
              data-testid={`channel-${channel.id}`}
            >
              {/* Favorite Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFavorite(channel.id);
                }}
                className="absolute top-1 right-1 sm:top-2 sm:right-2 z-10 p-1 sm:p-1.5 bg-black/70 rounded-full hover:bg-black transition-colors"
              >
                <Heart 
                  size={14} 
                  className={isFavorite(channel.id) ? "text-red-500" : "text-gray-400 hover:text-red-400"} 
                  fill={isFavorite(channel.id) ? "currentColor" : "none"}
                />
              </button>

              {/* Clickable area */}
              <button
                onClick={() => handlePlayChannel(channel)}
                className="w-full text-left"
              >
                {/* Channel Logo */}
                <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg mb-2 sm:mb-3 flex items-center justify-center overflow-hidden relative">
                  {channel.logo && (
                    <img 
                      src={`/api/iptv/image-proxy?url=${encodeURIComponent(channel.logo)}`}
                      alt={channel.name}
                      className="w-full h-full object-contain bg-gray-900 p-1"
                      onLoad={(e) => {
                        e.target.style.opacity = '1';
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.parentElement.querySelector('.fallback-icon');
                        if (fallback) fallback.style.display = 'flex';
                      }}
                      style={{ opacity: 0.5 }}
                    />
                  )}
                  <div 
                    className={`fallback-icon absolute inset-0 ${channel.logo ? 'hidden' : 'flex'} items-center justify-center bg-gradient-to-br from-cyan-900/50 to-gray-900`}
                  >
                    <div className="text-center px-2">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-cyan-600/30 flex items-center justify-center mx-auto mb-1 sm:mb-2">
                        <span className="text-cyan-400 text-lg sm:text-xl font-bold">
                          {channel.name?.charAt(0) || 'TV'}
                        </span>
                      </div>
                      <span className="text-gray-400 text-[8px] sm:text-[10px] font-medium line-clamp-2">{channel.name?.substring(0, 20)}</span>
                    </div>
                  </div>
                  {/* Channel name overlay for better identification */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-1.5 sm:p-2 pt-4 sm:pt-6">
                    <p className="text-white text-[8px] sm:text-[10px] font-semibold truncate drop-shadow-lg">
                      {channel.name?.replace(/^(SLING|PRIME|TUBI):\s*/i, '')}
                    </p>
                  </div>
                  {/* Play overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                      <Play size={20} className="text-black ml-0.5 sm:ml-1" fill="black" />
                    </div>
                  </div>
                  {/* Live indicator */}
                  <div className="absolute top-1 left-1 sm:top-2 sm:left-2">
                    <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-red-600 text-white text-[8px] sm:text-[10px] font-bold rounded-md flex items-center gap-0.5 sm:gap-1">
                      <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-white rounded-full animate-pulse"></span>
                      LIVE
                    </span>
                  </div>
                </div>

                {/* Channel Info */}
                <h3 className="text-white text-xs sm:text-sm font-medium truncate group-hover:text-cyan-400 transition-colors">
                  {channel.name}
                </h3>
                <p className="text-gray-500 text-xs truncate mt-1">{channel.category}</p>
              </button>
            </div>
          ))}
        </div>
      )
      )}

      {/* Empty State - only in grid mode */}
      {viewMode === 'grid' && !loading && channels.length === 0 && (
        <div className="bg-black border border-white/10 rounded-xl p-8 text-center">
          <Tv size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-300 mb-2">
            {selectedTab === 'favorites' ? 'No favorite channels' : 'No channels found'}
          </h3>
          <p className="text-gray-500 text-sm">
            {selectedTab === 'favorites' 
              ? 'Click the heart icon on any channel to add it to your favorites'
              : searchQuery 
                ? `No channels match "${searchQuery}"` 
                : `No channels available in ${CUSTOM_TABS.find(t => t.id === selectedTab)?.name || 'this category'}`}
          </p>
        </div>
      )}

      {/* Pagination - only in grid mode when there are multiple pages */}
      {viewMode === 'grid' && !loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 py-6">
          {/* Previous button */}
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className={`flex items-center gap-1 px-3 py-2 rounded-lg border transition-all ${
              currentPage === 1
                ? 'bg-gray-900 border-gray-700 text-gray-600 cursor-not-allowed'
                : 'bg-black border-white/30 text-white hover:border-white/60 hover:bg-white/10'
            }`}
          >
            <ChevronLeft size={18} />
            <span className="hidden sm:inline">Previous</span>
          </button>

          {/* Page numbers */}
          <div className="flex items-center gap-1">
            {/* First page */}
            {currentPage > 3 && (
              <>
                <button
                  onClick={() => goToPage(1)}
                  className="w-10 h-10 rounded-lg border border-white/30 text-white hover:border-white/60 hover:bg-white/10 transition-all"
                >
                  1
                </button>
                {currentPage > 4 && <span className="text-gray-500 px-2">...</span>}
              </>
            )}

            {/* Pages around current */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              if (pageNum < 1 || pageNum > totalPages) return null;
              
              return (
                <button
                  key={pageNum}
                  onClick={() => goToPage(pageNum)}
                  className={`w-10 h-10 rounded-lg border transition-all ${
                    currentPage === pageNum
                      ? 'bg-cyan-600 border-cyan-600 text-white'
                      : 'bg-black border-white/30 text-white hover:border-white/60 hover:bg-white/10'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            {/* Last page */}
            {currentPage < totalPages - 2 && totalPages > 5 && (
              <>
                {currentPage < totalPages - 3 && <span className="text-gray-500 px-2">...</span>}
                <button
                  onClick={() => goToPage(totalPages)}
                  className="w-10 h-10 rounded-lg border border-white/30 text-white hover:border-white/60 hover:bg-white/10 transition-all"
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>

          {/* Next button */}
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`flex items-center gap-1 px-3 py-2 rounded-lg border transition-all ${
              currentPage === totalPages
                ? 'bg-gray-900 border-gray-700 text-gray-600 cursor-not-allowed'
                : 'bg-black border-white/30 text-white hover:border-white/60 hover:bg-white/10'
            }`}
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight size={18} />
          </button>
        </div>
      )}

      {/* Channel Count - only in grid mode */}
      {viewMode === 'grid' && !loading && channels.length > 0 && (
        <div className="text-center text-gray-500 text-sm">
          Showing {((currentPage - 1) * CHANNELS_PER_PAGE) + 1} - {Math.min(currentPage * CHANNELS_PER_PAGE, filteredChannels.length)} of {filteredChannels.length} {CUSTOM_TABS.find(t => t.id === selectedTab)?.name || ''} channels
        </div>
      )}
    </div>
  );
};

export default LiveTVSection;
